import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
} from "@material-ui/core";
import PhoneInTalkIcon from "@material-ui/icons/PhoneInTalk";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import * as api from "../../api";
import { useSnackbar } from "../../utils/useSnackbar";
import { CardSkeleton } from "../Skeletons";
import AccessExpiredDialog from "../Login/AccessExpiredDialog";
import cardStyles from "../Settings/SettingsCard.module.css";
import styles from "./SupportContactTab.module.css";

// The two numbers a locked-out user is offered on the Access Expired screen.
// Kept as a panel of its own rather than another card in Global Settings
// because it's the only setting here that a *logged-out* person ever sees,
// and it wants room for the preview.
//
// The card itself is read-only; changes go through the Edit dialog. These are
// the only lifeline a locked-out store has, so changing them is a deliberate
// act rather than something a stray keystroke can do.

const validateNumber = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "Required - locked-out users have no other way to reach you.";
  return "";
};

const SupportContactTab = () => {
  const [openSnackbar] = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [businessName, setBusinessName] = useState(null);
  // Draft values - only meaningful while the edit dialog is open.
  const [callNumber, setCallNumber] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  // The persisted values, for the dirty check below - Save stays disabled
  // until something actually differs, so it can't be pressed by reflex.
  const [saved, setSaved] = useState({ call: "", whatsapp: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, brandRes] = await Promise.all([
          api.getGlobalSettings(),
          api.fetchPublicBusinessName(),
        ]);
        if (cancelled) return;
        const s = settingsRes.data?.data || {};
        setCallNumber(s.supportCallNumber || "");
        setWhatsappNumber(s.supportWhatsappNumber || "");
        setSaved({ call: s.supportCallNumber || "", whatsapp: s.supportWhatsappNumber || "" });
        setBusinessName(brandRes.data?.businessName || null);
      } catch (error) {
        if (!cancelled) openSnackbar("Failed to load support contact settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callError = validateNumber(callNumber);
  const whatsappError = validateNumber(whatsappNumber);
  const isDirty = callNumber.trim() !== saved.call || whatsappNumber.trim() !== saved.whatsapp;
  const canSave = !callError && !whatsappError && isDirty && !saving;
  const isIncomplete = !saved.call || !saved.whatsapp;

  const openEditor = () => {
    // Always start from what's persisted, so an abandoned edit can't leak
    // into the next one.
    setCallNumber(saved.call);
    setWhatsappNumber(saved.whatsapp);
    setEditing(true);
  };

  const closeEditor = () => {
    if (saving) return;
    setCallNumber(saved.call);
    setWhatsappNumber(saved.whatsapp);
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.updateGlobalSettings({
        supportCallNumber: callNumber.trim(),
        supportWhatsappNumber: whatsappNumber.trim(),
      });
      const s = data?.data || {};
      setSaved({
        call: s.supportCallNumber ?? callNumber.trim(),
        whatsapp: s.supportWhatsappNumber ?? whatsappNumber.trim(),
      });
      setEditing(false);
      openSnackbar("Support contact updated - it applies to every store immediately");
    } catch (error) {
      openSnackbar(error?.response?.data?.message || "Failed to save support contact");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CardSkeleton />;
  }

  // While editing, the preview shows the draft - the point of it is to check a
  // number *before* committing it to every store.
  const previewCall = (editing ? callNumber : saved.call).trim();
  const previewWhatsapp = (editing ? whatsappNumber : saved.whatsapp).trim();

  const renderNumber = (kind, label, value, hint) => (
    <div className={styles.numberRow}>
      <span
        className={`${styles.numberIcon} ${
          kind === "call" ? styles.numberIconCall : styles.numberIconWhatsapp
        }`}
      >
        {kind === "call" ? <PhoneInTalkIcon fontSize="small" /> : <WhatsAppIcon fontSize="small" />}
      </span>
      <div className={styles.numberText}>
        <span className={styles.numberLabel}>{label}</span>
        <span className={value ? styles.numberValue : styles.numberValueEmpty}>
          {value || "Not set"}
        </span>
        <span className={styles.numberHint}>{hint}</span>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 720 }}>
      <div className={cardStyles.card}>
        <div className={cardStyles.cardHeader}>
          <span className={`${cardStyles.cardIcon} ${cardStyles.cardIconTeal}`}>
            <PhoneInTalkIcon fontSize="small" />
          </span>
          <Typography className={cardStyles.cardTitle}>Support Contact</Typography>
        </div>

        <div className={cardStyles.cardBody} style={{ padding: "16px 20px" }}>
          <Typography variant="body2" style={{ color: "#64748b", marginBottom: 4, lineHeight: 1.5 }}>
            Shown on the <strong>Access Expired</strong> screen when a store's licence has
            lapsed. These are the only numbers a locked-out user can reach you on, and they
            apply to every store in this deployment.
          </Typography>

          {isIncomplete && (
            <div className={styles.warningStrip}>
              <ErrorOutlineIcon fontSize="small" />
              <span>
                A support number is missing - locked-out users currently have no way to reach you.
              </span>
            </div>
          )}

          <div className={styles.numberList}>
            {renderNumber("call", "Call number", saved.call, "Dialled by the 'Call support' button.")}
            {renderNumber(
              "whatsapp",
              "WhatsApp number",
              saved.whatsapp,
              "Opens WhatsApp with a pre-filled message naming the store."
            )}
          </div>
        </div>

        <div className={cardStyles.cardActions}>
          <Button
            className={cardStyles.cancelBtn}
            style={{ marginRight: "auto", padding: "6px 16px", fontSize: "0.82rem" }}
            disableElevation
            onClick={() => setPreviewOpen(true)}
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
          >
            Preview lock-out screen
          </Button>
          <Button
            className={cardStyles.editButton}
            disableElevation
            onClick={openEditor}
            startIcon={<EditOutlinedIcon fontSize="small" />}
          >
            Edit
          </Button>
        </div>
      </div>

      <Dialog
        open={editing}
        onClose={closeEditor}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 20,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
          },
        }}
      >
        <div className={cardStyles.dialogHeader}>
          <div className={cardStyles.dialogHeaderLeft}>
            <PhoneInTalkIcon />
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Edit Support Contact
            </Typography>
          </div>
          <IconButton
            size="small"
            className={cardStyles.dialogCloseBtn}
            onClick={closeEditor}
            disabled={saving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <DialogContent className={cardStyles.dialogBody}>
          <div className={styles.dialogNote}>
            <InfoOutlinedIcon fontSize="small" />
            <span>
              Saving applies these numbers to every store in this deployment immediately. Use{" "}
              <strong>Preview</strong> to see them on the lock-out screen first.
            </span>
          </div>

          <div className={cardStyles.dialogSection}>
            <Typography className={cardStyles.dialogSectionTitle}>Call number</Typography>
            <TextField
              value={callNumber}
              onChange={(e) => setCallNumber(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              autoFocus
              disabled={saving}
              error={!!callError}
              helperText={callError || "Dialled by the 'Call support' button."}
              placeholder="+91 94140 64844"
              InputProps={{
                startAdornment: (
                  <PhoneInTalkIcon fontSize="small" style={{ color: "#94a3b8", marginRight: 8 }} />
                ),
              }}
            />
          </div>

          <div className={cardStyles.dialogSection}>
            <Typography className={cardStyles.dialogSectionTitle}>WhatsApp number</Typography>
            <TextField
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              disabled={saving}
              error={!!whatsappError}
              helperText={
                whatsappError || "Opens WhatsApp with a pre-filled message naming the store."
              }
              placeholder="+91 94140 64844"
              InputProps={{
                startAdornment: (
                  <WhatsAppIcon fontSize="small" style={{ color: "#25d366", marginRight: 8 }} />
                ),
              }}
            />
          </div>
        </DialogContent>

        <DialogActions className={cardStyles.dialogActions}>
          {/* Previews the draft values, not the saved ones - the point is to
              check a number before committing it to every store. */}
          <Button
            className={cardStyles.cancelBtn}
            style={{ marginRight: "auto" }}
            disableElevation
            onClick={() => setPreviewOpen(true)}
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
          >
            Preview
          </Button>
          <Button
            className={`${cardStyles.saveBtn} ${!canSave ? styles.saveBtnDisabled : ""}`}
            disableElevation
            onClick={handleSave}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
          <Button
            className={cardStyles.cancelBtn}
            disableElevation
            onClick={closeEditor}
            disabled={saving}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <AccessExpiredDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        // Word-for-word what middleware/expiryDate.js sends a lapsed store,
        // so the preview isn't flattering fiction.
        message={`For assistance, please contact our support team at ${previewCall || "…"}.`}
        businessName={businessName}
        callNumber={previewCall}
        whatsappNumber={previewWhatsapp}
      />
    </div>
  );
};

export default SupportContactTab;
